import axios from 'axios';

interface VisitData {
  timestamp: string;
  url: string;
  referrer: string;
  userAgent: string;
  language: string;
  screenWidth: number;
  screenHeight: number;
  timezone: string;
}

/**
 * Collects visitor information
 */
const collectVisitData = (): VisitData => {
  return {
    timestamp: new Date().toISOString(),
    url: window.location.href,
    referrer: document.referrer || 'Direct visit',
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

/**
 * Formats visit data for Discord webhook
 */
const formatDiscordMessage = (data: VisitData): any => {
  const date = new Date(data.timestamp);
  const formattedDate = date.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return {
    embeds: [
      {
        title: '🌐 New Site Visit!',
        color: 0x00d4ff, // Cyan color
        fields: [
          {
            name: '📅 Time',
            value: formattedDate,
            inline: true,
          },
          {
            name: '🌍 Timezone',
            value: data.timezone,
            inline: true,
          },
          {
            name: '🔗 Page',
            value: data.url,
            inline: false,
          },
          {
            name: '🔙 Referrer',
            value: data.referrer.length > 100 
              ? data.referrer.substring(0, 100) + '...' 
              : data.referrer,
            inline: false,
          },
          {
            name: '💻 Device',
            value: `${data.screenWidth}x${data.screenHeight}`,
            inline: true,
          },
          {
            name: '🌐 Language',
            value: data.language,
            inline: true,
          },
        ],
        footer: {
          text: 'Portfolio Site Tracker',
        },
        timestamp: data.timestamp,
      },
    ],
  };
};

/**
 * Formats visit data for email/webhook (plain text)
 */
const formatPlainMessage = (data: VisitData): string => {
  const date = new Date(data.timestamp);
  const formattedDate = date.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return `
🌐 New Site Visit!

📅 Time: ${formattedDate}
🌍 Timezone: ${data.timezone}
🔗 Page: ${data.url}
🔙 Referrer: ${data.referrer}
💻 Device: ${data.screenWidth}x${data.screenHeight}
🌐 Language: ${data.language}
🖥️ User Agent: ${data.userAgent}
  `.trim();
};

/**
 * Sends notification via Discord webhook
 */
const sendDiscordNotification = async (data: VisitData): Promise<void> => {
  const webhookUrl = process.env.REACT_APP_DISCORD_WEBHOOK_URL;
  
  if (!webhookUrl) {
    console.warn('Discord webhook URL not configured');
    return;
  }

  try {
    const message = formatDiscordMessage(data);
    await axios.post(webhookUrl, message, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error('Failed to send Discord notification:', error);
  }
};

/**
 * Sends notification via custom webhook/API
 */
const sendCustomWebhook = async (data: VisitData): Promise<void> => {
  const webhookUrl = process.env.REACT_APP_CUSTOM_WEBHOOK_URL;
  
  if (!webhookUrl) {
    return;
  }

  try {
    await axios.post(webhookUrl, {
      type: 'site_visit',
      ...data,
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error('Failed to send custom webhook notification:', error);
  }
};

/**
 * Sends notification via EmailJS (if configured)
 */
const sendEmailNotification = async (data: VisitData): Promise<void> => {
  const emailServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const emailTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const emailPublicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
  const recipientEmail = process.env.REACT_APP_NOTIFICATION_EMAIL;

  if (!emailServiceId || !emailTemplateId || !emailPublicKey || !recipientEmail) {
    return;
  }

  try {
    // Dynamic import to avoid bundling EmailJS if not used
    const emailjs = await import('@emailjs/browser');
    
    await emailjs.send(
      emailServiceId,
      emailTemplateId,
      {
        to_email: recipientEmail,
        subject: 'New Site Visit - Portfolio',
        message: formatPlainMessage(data),
        visit_url: data.url,
        visit_time: new Date(data.timestamp).toLocaleString(),
      },
      emailPublicKey
    );
  } catch (error) {
    console.error('Failed to send email notification:', error);
  }
};

/**
 * Main function to track and notify about a visit
 * Only tracks once per session to avoid spam
 */
let hasTrackedThisSession = false;

export const trackVisit = async (): Promise<void> => {
  // Prevent multiple notifications in the same session
  if (hasTrackedThisSession) {
    return;
  }

  // Check if tracking is disabled
  if (process.env.REACT_APP_DISABLE_VISIT_TRACKING === 'true') {
    return;
  }

  hasTrackedThisSession = true;

  const visitData = collectVisitData();

  // Send notifications in parallel (all methods are optional)
  await Promise.allSettled([
    sendDiscordNotification(visitData),
    sendCustomWebhook(visitData),
    sendEmailNotification(visitData),
  ]);
};

